import React, { useState, useRef } from 'react';
import { storageService } from '@/services/storage.service';
import { toast } from 'sonner';
import { cn } from '@/utils/cn';
import { ProjectAttachments } from './ProjectAttachments';

interface ProjectSidebarInfoProps {
  form: any;
  updateForm: (data: any) => void;
  isSaving: boolean;
  onCancel: () => void;
  thumbnailFile?: File | null;
  setThumbnailFile?: (file: File | null) => void;
  attachmentFiles?: File[];
  setAttachmentFiles?: (files: File[]) => void;
}

export function ProjectSidebarInfo({ form, updateForm, isSaving, onCancel, thumbnailFile, setThumbnailFile, attachmentFiles, setAttachmentFiles }: ProjectSidebarInfoProps) {
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error('File không được vượt quá 5MB');
      return;
    }

    if (setThumbnailFile) {
      setThumbnailFile(file);
      // Tạo preview URL tạm thời
      updateForm({ thumbnail: URL.createObjectURL(file) });
    }
    
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <div className="space-y-4">
      {/* Trạng thái */}
      <section className="rounded-md bg-white p-4  border border-slate-100 space-y-3">
        <label className="text-xs lg:text-sm font-normal text-slate-700 block">Trạng thái dự án <span className="text-rose-500">*</span></label>
        <select 
          value={form.statusCode || 'planning'}
          onChange={e => updateForm({ statusCode: e.target.value })}
          className="h-10 w-full rounded-md border border-slate-200 bg-emerald-50 text-emerald-600 px-3 text-xs lg:text-sm font-normal outline-none focus:border-emerald-500 appearance-none cursor-pointer"
        >
          <option value="planning">Đang chuẩn bị</option>
          <option value="active">Đang thực hiện</option>
          <option value="completed">Đã hoàn thành</option>
        </select>
      </section>

      {/* Độ ưu tiên */}
      <section className="rounded-md bg-white p-4  border border-slate-100 space-y-3">
        <label className="text-xs lg:text-sm font-normal text-slate-700 block">Độ ưu tiên</label>
        <div className="space-y-3">
          <PriorityRadio 
            label="Cao" 
            bgColor="bg-rose-500" 
            borderColor="border-rose-500"
            textColor="text-rose-600"
            checked={form.priority === 'high'} 
            onChange={() => updateForm({ priority: 'high' })} 
          />
          <PriorityRadio 
            label="Trung bình" 
            bgColor="bg-amber-500" 
            borderColor="border-amber-500"
            textColor="text-amber-600"
            checked={form.priority === 'medium'} 
            onChange={() => updateForm({ priority: 'medium' })} 
          />
          <PriorityRadio 
            label="Thấp" 
            bgColor="bg-emerald-500" 
            borderColor="border-emerald-500"
            textColor="text-emerald-600"
            checked={form.priority === 'low'} 
            onChange={() => updateForm({ priority: 'low' })} 
          />
        </div>
      </section>

      {/* Hình ảnh đại diện */}
      <section className="rounded-md bg-white p-4  border border-slate-100 space-y-3">
        <label className="text-xs lg:text-sm font-normal text-slate-700 block">Hình ảnh đại diện</label>
        
        <input 
          type="file" 
          ref={fileInputRef} 
          onChange={handleFileChange} 
          accept="image/jpeg, image/png, image/webp" 
          className="hidden" 
        />

        {form.thumbnail ? (
          <div className="relative aspect-video rounded-md overflow-hidden border border-slate-100 group">
            <img src={form.thumbnail} alt="Thumbnail" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-slate-900/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
               <button 
                 type="button" 
                 onClick={() => {
                   updateForm({ thumbnail: '' });
                   if (setThumbnailFile) setThumbnailFile(null);
                 }}
                 className="h-8 px-3 rounded-md bg-white text-xs font-normal text-rose-500 hover:bg-rose-50 transition-colors flex items-center gap-2 "
               >
                 <i className="ri-delete-bin-line" /> Gỡ ảnh
               </button>
            </div>
          </div>
        ) : (
          <div 
            onClick={handleUploadClick}
            className="aspect-video rounded-md border-2 border-dashed border-slate-50 bg-slate-50/30 flex flex-col items-center justify-center gap-3 hover:border-emerald-200 transition-all cursor-pointer group"
          >
            <i className="ri-image-add-line text-3xl text-slate-200 group-hover:text-emerald-300" />
            <button type="button" className="px-4 py-1.5 rounded-md bg-white border border-slate-200 text-xs lg:text-sm font-normal text-slate-600  hover:bg-slate-50 transition-colors pointer-events-none">
              Chọn ảnh
            </button>
          </div>
        )}
        
        <h3 className="text-sm font-normal text-slate-900 tracking-wider">Thông tin tóm tắt</h3>
        <p className="text-xs text-slate-400 font-normal italic text-center">
          Định dạng: JPG, PNG (Tối đa 5MB)
        </p>
      </section>

      {/* Tài liệu đính kèm */}
      <ProjectAttachments 
        attachments={form.attachments} 
        onAdd={(url) => updateForm({ attachments: [...(form.attachments || []), url] })}
        onRemove={(url) => updateForm({ attachments: form.attachments.filter((a: string) => a !== url) })}
        attachmentFiles={attachmentFiles}
        setAttachmentFiles={setAttachmentFiles}
      />

      {/* Ghi chú */}
      <section className="rounded-md bg-white p-4  border border-slate-100 space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-xs font-normal text-slate-400">Tên dự án:</span>
          <span className="text-xs lg:text-sm font-normal text-slate-700 truncate">{form.name || '---'}</span>
          <span className="text-xs text-slate-400 font-normal">{(form.note || '').length}/300</span>
        </div>
        <textarea 
          rows={5}
          value={form.note || ''}
          onChange={e => updateForm({ note: e.target.value })}
          placeholder="Nhập ghi chú (nếu có)"
          className="w-full rounded-md border border-slate-200 p-3 text-xs lg:text-sm font-normal text-slate-700 outline-none focus:border-emerald-500 transition-all resize-none bg-slate-50/20"
        />
      </section>

      {/* Action Buttons */}
      <div className="grid grid-cols-2 gap-3 pt-2">
        <button 
          type="button"
          onClick={onCancel}
          className="h-11 w-full rounded-md border border-slate-200 bg-white text-xs lg:text-sm font-normal text-slate-500 hover:bg-slate-50 hover:text-slate-700 transition-all active:scale-95 "
        >
          Hủy bỏ
        </button>
        <button 
          type="submit"
          form="project-form"
          disabled={isSaving}
          className="h-11 w-full rounded-md bg-emerald-700 text-xs lg:text-sm font-normal text-white hover:bg-emerald-800 transition-all  shadow-emerald-700/20 active:scale-95 disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {isSaving ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-save-line text-lg" />}
          Lưu dự án
        </button>
      </div>
    </div>
  );
}

function PriorityRadio({ label, bgColor, borderColor, textColor, checked, onChange }: { label: string; bgColor: string; borderColor: string; textColor: string; checked: boolean; onChange: () => void }) {
  return (
    <label className="flex items-center gap-2 cursor-pointer group">
      <input type="radio" className="hidden" checked={checked} onChange={onChange} />
      <div className={cn(
        "w-4 h-4 rounded-full border-2 flex items-center justify-center transition-all",
        checked ? borderColor : "border-slate-200 group-hover:border-slate-300"
      )}>
        {checked && <div className={cn("w-1.5 h-1.5 rounded-full", bgColor)} />}
      </div>
      <span className={cn(
        "text-xs lg:text-sm transition-colors",
        checked ? cn(textColor, "font-medium") : "text-slate-500 group-hover:text-slate-700 font-normal"
      )}>{label}</span>
    </label>
  );
}
